import moment from 'moment'
import { FC } from 'react'
import en from 'src/constants/en'
import { BASE_API_DATE_FORMAT } from 'src/constants/dateFormats'
import { AnalyticsFetchCalendarDetailsResponse } from 'src/services/__generated__/api'
import { PositivityLegend } from 'src/components/PositivityLegend'
import { PositivityTypes } from 'src/components/PositivityLegend/enums/positivityTypes.enum'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import { SectionTitle } from 'src/components/SectionTitle'
import { EmptyState } from 'src/components/EmptyState'
import { Skeleton } from 'src/components/Skeleton'
import styles from './calendarDetails.module.scss'
import { Mentions } from './Mentions'
import { TopSources } from './TopSources'
import { TopCampaigns } from './TopCampaigns'
import { MentionsChart } from './MentionsChart'
import { ChartNavigation } from '../ChartNavigation'

// Real dimensions taken from the widget's own styles: the donut is 172x172
// (mentionsChart.module.scss) and each list row is a 16px line with 8px
// padding-top under a 12px/14px title (topSources + mentions module.scss).
// Column min-widths come from calendarDetails.module.scss.
const MENTIONS_CHART_SIZE = 172
const LIST_ROW_HEIGHT = 16
const LIST_COLUMNS = [
  { key: 'mentions' as const, rows: 4, width: 140 },
  { key: 'sources' as const, rows: 4, width: 112 },
  { key: 'campaigns' as const, rows: 4, width: 90 },
]

interface CalendarDetailsProps {
  date?: string
  isLoading?: boolean
  navigateDays: (date: string) => void
  selectedDateData: AnalyticsFetchCalendarDetailsResponse
}

export const CalendarDetails: FC<CalendarDetailsProps> = ({
  selectedDateData,
  navigateDays,
  date,
  isLoading,
}) => {
  const selectedDate = moment(date)
  const parsedDate = selectedDate.format('MMMM DD, YYYY')
  const { data: informationTooltips } = useGetInformationTooltipsQuery()

  const negatives = selectedDateData?.result?.negatives
  const neutrals = selectedDateData?.result?.neutrals
  const positives = selectedDateData?.result?.positives
  const total = selectedDateData?.result?.total
  const negPercentage = selectedDateData?.result?.negPercentage
  const posPercentage = selectedDateData?.result?.posPercentage
  const neuPercentage = selectedDateData?.result?.neuPercentage

  const hasData = !!(
    negatives ||
    neutrals ||
    positives ||
    total ||
    selectedDateData?.topSources?.length ||
    selectedDateData?.topCampaigns?.length
  )

  // Skeletons stand in for the first load only. On a refetch (arrowing to
  // another day) the previous day's figures stay up until the new ones land.
  // Keyed on hasData rather than `result` alone: result/topSources/topCampaigns
  // are independently optional in the response, so a day carrying only sources
  // or campaigns would otherwise flash skeletons over content on every refetch.
  const isInitialLoading = !!isLoading && !hasData

  return (
    <div className={styles.calendarDetails}>
      <SectionTitle
        isLoading={isLoading}
        title={en.calendarDetails}
        tooltipContent={informationTooltips?.calendarDetails}
      />

      <div className={styles.navigation}>
        <ChartNavigation
          onLeftClick={() =>
            navigateDays(
              selectedDate.subtract(1, 'day').format(BASE_API_DATE_FORMAT)
            )
          }
          onRightClick={() =>
            navigateDays(
              selectedDate.add(1, 'day').format(BASE_API_DATE_FORMAT)
            )
          }
          title={parsedDate}
        />
      </div>

      {isInitialLoading ? (
        <div className={styles.container}>
          <Skeleton circle width={MENTIONS_CHART_SIZE} />
          <div className={styles.lists}>
            {LIST_COLUMNS.map(({ key, rows, width }) => (
              <div className={styles[key]} key={key}>
                <div className={styles.skeletonListTitle}>
                  <Skeleton height={14} radius={4} width={64} />
                </div>
                {Array.from({ length: rows }).map((_, index) => (
                  <div
                    className={styles.skeletonListItem}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                  >
                    <Skeleton
                      height={LIST_ROW_HEIGHT}
                      radius={4}
                      width={width}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : hasData ? (
        <div className={styles.container}>
          <div>
            {(!!negatives || !!neutrals || !!positives) && (
              <MentionsChart
                negatives={negatives}
                negativesPercentage={negPercentage ?? 0}
                neutrals={neutrals}
                neutralsPercentage={neuPercentage ?? 0}
                positives={positives}
                positivesPercentage={posPercentage ?? 0}
              />
            )}
          </div>

          <div className={styles.lists}>
            {!!total && (
              <div className={styles.mentions}>
                <Mentions
                  negativeCount={negatives}
                  neutralCount={neutrals}
                  positiveCount={positives}
                  total={total}
                />
              </div>
            )}

            {!!selectedDateData?.topSources?.length && (
              <div className={styles.sources}>
                <TopSources sources={selectedDateData.topSources} />
              </div>
            )}

            {!!selectedDateData?.topCampaigns?.length && (
              <div className={styles.campaigns}>
                <TopCampaigns campaigns={selectedDateData.topCampaigns} />
              </div>
            )}
          </div>
        </div>
      ) : !isLoading ? (
        <div className={styles.emptyContainer}>
          <EmptyState
            description={en.noCalendarDetailsDescription}
            icon="calendar"
            title={en.noCalendarDetailsData}
          />
        </div>
      ) : null}

      <div className={styles.footer}>
        <PositivityLegend
          types={[
            PositivityTypes.POSITIVE,
            PositivityTypes.NEUTRAL,
            PositivityTypes.NEGATIVE,
          ]}
        />
      </div>
    </div>
  )
}
